import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { type ApiError, api, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type { Items, Member, Role } from '../api/types';
import {
  Badge,
  Card,
  DataTable,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  useToast,
} from '../components/ui';

const ALL_PERMISSIONS = [
  'automation:read',
  'automation:create',
  'automation:update',
  'automation:delete',
  'automation:publish',
  'automation:pause',
  'flow:read',
  'flow:manage',
  'account:read',
  'account:connect',
  'account:manage',
  'conversation:read',
  'conversation:reply',
  'contact:read',
  'contact:manage',
  'analytics:read',
  'apikey:create',
  'webhook:manage',
  'team:manage',
  'billing:manage',
  'settings:manage',
  'workspace:delete',
];

export function TeamPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => unwrap(api.get<Items<Member>>('/portal/team/members')),
  });
  const accounts = useAccounts();
  const automations = useAutomations();
  // IAM-10/11: a multi-select where the empty selection means "all"
  const setScope = useMutation({
    mutationFn: (v: {
      userId: string;
      field: 'accountIds' | 'automationIds';
      ids: string[] | null;
    }) => unwrap(api.patch(`/portal/team/members/${v.userId}`, { [v.field]: v.ids })),
    onSuccess: () => refresh(),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const scopeSelect = (
    m: Member,
    field: 'accountIds' | 'automationIds',
    options: { id: string; label: string }[],
  ) => (
    <select
      className="input"
      multiple
      size={Math.min(Math.max(options.length + 1, 2), 4)}
      aria-label={`${field === 'accountIds' ? 'accounts' : 'automations'} for ${m.email}`}
      value={m[field] ?? ['*']}
      onChange={(e) => {
        const picked = [...e.target.selectedOptions].map((o) => o.value);
        const ids = picked.includes('*') || picked.length === 0 ? null : picked;
        setScope.mutate({ userId: m.userId, field, ids });
      }}
    >
      <option value="*">All</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => unwrap(api.get<Items<Role>>('/portal/team/roles')),
  });
  const [adding, setAdding] = useState(false);
  const [roleModal, setRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', role: 'operator', roleId: '' });
  const [roleForm, setRoleForm] = useState<{ name: string; permissions: string[] }>({
    name: '',
    permissions: [],
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['members'] }),
      qc.invalidateQueries({ queryKey: ['roles'] }),
    ]);
  const add = useMutation({
    mutationFn: () =>
      unwrap(api.post('/portal/team/members', { ...form, roleId: form.roleId || null })),
    onSuccess: async () => {
      await refresh();
      setAdding(false);
      toast('Member added');
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => unwrap(api.del(`/portal/team/members/${userId}`)),
    onSuccess: async () => {
      await refresh();
      toast('Member removed');
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role?: string; roleId?: string | null }) =>
      unwrap(api.patch(`/portal/team/members/${v.userId}`, { role: v.role, roleId: v.roleId })),
    onSuccess: () => refresh(),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const createRole = useMutation({
    mutationFn: () =>
      editingRole
        ? unwrap(api.patch(`/portal/team/roles/${editingRole}`, roleForm))
        : unwrap(api.post('/portal/team/roles', roleForm)),
    onSuccess: async () => {
      await refresh();
      setRoleModal(false);
      setEditingRole(null);
      setRoleForm({ name: '', permissions: [] });
      toast(editingRole ? 'Role updated' : 'Role created');
    },
  });
  const deleteRole = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/team/roles/${id}`)),
    onSuccess: () => refresh(),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Members, roles and permissions"
        actions={
          <>
            <button type="button" className="btn" onClick={() => setRoleModal(true)}>
              New role
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              Add member
            </button>
          </>
        }
      />
      <ErrorAlert error={members.error} />
      <Card title="Members">
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Custom role</th>
                <th>Accounts</th>
                <th>Automations</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(members.data?.items ?? []).map((m) => (
                <tr key={m.userId}>
                  <td>
                    {m.email}
                    {m.pending ? (
                      // IAM-21 (revised): the owner sees which address an unaccepted invite reserves
                      <>
                        {' '}
                        <Badge value="invite pending" />
                      </>
                    ) : null}
                  </td>
                  <td>
                    {m.role === 'owner' ? (
                      <Badge value="owner" />
                    ) : (
                      <select
                        className="input"
                        value={m.role}
                        onChange={(e) =>
                          changeRole.mutate({ userId: m.userId, role: e.target.value })
                        }
                      >
                        {['manager', 'operator', 'developer'].map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.role === 'owner' ? (
                      '—'
                    ) : (
                      <select
                        className="input"
                        value={m.roleId ?? ''}
                        onChange={(e) =>
                          changeRole.mutate({ userId: m.userId, roleId: e.target.value || null })
                        }
                      >
                        <option value="">none</option>
                        {(roles.data?.items ?? []).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.role === 'owner'
                      ? 'all'
                      : scopeSelect(
                          m,
                          'accountIds',
                          (accounts.data?.items ?? []).map((a) => ({
                            id: a.id,
                            label: `@${a.username}`,
                          })),
                        )}
                  </td>
                  <td>
                    {m.role === 'owner'
                      ? 'all'
                      : scopeSelect(
                          m,
                          'automationIds',
                          (automations.data?.items ?? []).map((a) => ({ id: a.id, label: a.name })),
                        )}
                  </td>
                  <td>{fmtDate(m.createdAt)}</td>
                  <td className="actions">
                    {m.role !== 'owner' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => remove.mutate(m.userId)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </Card>
      <Card title="Custom roles">
        <DataTable>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(roles.data?.items ?? []).map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="pill-row">
                    {r.permissions.map((p) => (
                      <span key={p} className="badge">
                        {p}
                      </span>
                    ))}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setEditingRole(r.id);
                        setRoleForm({ name: r.name, permissions: [...r.permissions] });
                        setRoleModal(true);
                      }}
                    >
                      Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => deleteRole.mutate(r.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {roles.data?.items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="muted">
                    No custom roles yet. Built-in roles: owner, manager, operator, developer.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </DataTable>
      </Card>
      {adding ? (
        <Modal
          title="Add member"
          onClose={() => setAdding(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={add.isPending}
                onClick={() => add.mutate()}
              >
                Add
              </button>
            </>
          }
        >
          <ErrorAlert error={add.error as ApiError | null} />
          <Field label="Email">
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <p className="muted">
            They will receive an email with a code and set their own password at{' '}
            <Link to="/reset">/reset</Link>; nobody else ever knows it.
          </p>
          <div className="row">
            <Field label="Role">
              <select
                className="input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {['manager', 'operator', 'developer'].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </Field>
            <Field label="Custom role (optional)">
              <select
                className="input"
                value={form.roleId}
                onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              >
                <option value="">none</option>
                {(roles.data?.items ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Modal>
      ) : null}
      {roleModal ? (
        <Modal
          title={editingRole ? 'Edit role' : 'Create role'}
          onClose={() => {
            setRoleModal(false);
            setEditingRole(null);
          }}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setRoleModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={createRole.isPending}
                onClick={() => createRole.mutate()}
              >
                {editingRole ? 'Save' : 'Create'}
              </button>
            </>
          }
        >
          <ErrorAlert error={createRole.error as ApiError | null} />
          <Field label="Role name">
            <input
              className="input"
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
            />
          </Field>
          <Field label="Platform features this role can use" group>
            <div className="grid grid-3">
              {ALL_PERMISSIONS.map((p) => (
                <label key={p} className="checkbox small">
                  <input
                    type="checkbox"
                    checked={roleForm.permissions.includes(p)}
                    onChange={() =>
                      setRoleForm({
                        ...roleForm,
                        permissions: roleForm.permissions.includes(p)
                          ? roleForm.permissions.filter((x) => x !== p)
                          : [...roleForm.permissions, p],
                      })
                    }
                  />{' '}
                  {p}
                </label>
              ))}
            </div>
          </Field>
        </Modal>
      ) : null}
    </>
  );
}
